import type { CargoRewardTerms } from "./reward-rules.js";
import type { ZoneLossTerms } from "./combat-rules.js";
import type { DeathSettlementTerms } from "./death-economy.js";
import type { PublicEventResolution } from "./events.js";
import type { TruceCrime, TruceAuthority } from "./truce-state.js";
import type { BatteryTerms, RocketTerms, DroneTerms } from "./supplies.js";
import { REGIONS, type SectorId } from "./world-layout.js";

export const LOOT_CARGO_KINDS = ["energy", "salvage", "contraband"] as const;
export type LootCargoKind = (typeof LOOT_CARGO_KINDS)[number];
export const CARGO_KINDS = [...LOOT_CARGO_KINDS, "battery", "rocket", "drone"] as const;
export type CargoKind = (typeof CARGO_KINDS)[number];
export interface LootCargoPayload {
  kind: LootCargoKind;
  rewardTerms?: CargoRewardTerms;
  supplyTerms?: never;
}
export interface BatteryCargoPayload {
  kind: "battery";
  supplyTerms: BatteryTerms;
  rewardTerms?: never;
}
export interface RocketCargoPayload { kind: "rocket"; supplyTerms: RocketTerms; rewardTerms?: never }
export interface DroneCargoPayload { kind: "drone"; supplyTerms: DroneTerms; rewardTerms?: never }
export type SupplyCargoPayload = BatteryCargoPayload | RocketCargoPayload | DroneCargoPayload;
export type CargoPayload = LootCargoPayload | SupplyCargoPayload;
interface CargoIdentity {
  id: string;
  sourceId: string;
}
export type LootCargoItem = CargoIdentity & LootCargoPayload;
export type CargoItem = CargoIdentity & CargoPayload;
export type SupplyCargoItem = CargoIdentity & SupplyCargoPayload;
export function isSupplyCargo<T extends CargoPayload>(item: T): item is T & SupplyCargoPayload { return item.kind === "battery" || item.kind === "rocket" || item.kind === "drone"; }
export function isLootCargo(item: CargoItem): item is LootCargoItem { return !isSupplyCargo(item); }
export type RegionRisk = "safe-pve" | "risky-pve" | "open-pvp";
export interface ZoneRules {
  risk: RegionRisk;
  pvpAllowed: boolean;
  ordinaryLossFraction: number;
  lossDisposition: "keep" | "destroy" | "drop";
}
const FRONTIER_RULES: ZoneRules = { risk: "open-pvp", pvpAllowed: true, ordinaryLossFraction: 0.5, lossDisposition: "drop" };
export const ZONE_RULES: Readonly<Record<SectorId, ZoneRules>> = {
  training: { risk: "safe-pve", pvpAllowed: false, ordinaryLossFraction: 0, lossDisposition: "keep" },
  patrol: { risk: "safe-pve", pvpAllowed: false, ordinaryLossFraction: 0, lossDisposition: "keep" },
  wrecks: { risk: "open-pvp", pvpAllowed: true, ordinaryLossFraction: 0.25, lossDisposition: "destroy" },
  frontier: FRONTIER_RULES,
  "concord-azure": FRONTIER_RULES,
  "concord-kepler": FRONTIER_RULES,
  "concord-beacons": FRONTIER_RULES,
  "concord-aegis": FRONTIER_RULES,
  "concord-harmony": FRONTIER_RULES,
  "helion-iron": FRONTIER_RULES,
  "helion-slag": FRONTIER_RULES,
  "helion-forge": FRONTIER_RULES,
  "helion-imperative": FRONTIER_RULES,
  "helion-rift": FRONTIER_RULES,
  "nomads-magpie": FRONTIER_RULES,
  "nomads-graveyard": FRONTIER_RULES,
  "nomads-mist": FRONTIER_RULES,
  "nomads-shards": FRONTIER_RULES,
  "nomads-last-chance": FRONTIER_RULES,
};
export function regionRisk(id: string): RegionRisk {
  const region = REGIONS.find((item) => item.id === id);
  return region ? ZONE_RULES[region.id].risk : "safe-pve";
}
export function regionPvpAllowed(id: string): boolean {
  return REGIONS.find((region) => region.id === id)?.pvpAllowed ?? false;
}
export const CARGO_REWARDS: Record<LootCargoKind, { credits: string; components: number }> = {
  energy: { credits: "60", components: 1 },
  salvage: { credits: "100", components: 3 },
  contraband: { credits: "300", components: 6 },
};
export const RESPAWN_SECONDS = 8;
export const DROP_TTL_MS = 600_000;
export const PVP_COMBAT_LOCK_SECONDS = 8;
export const INTEREST_ENTER_RADIUS = 1050;
export const INTEREST_EXIT_RADIUS = 1150;
export const CONTRABAND_RESPAWN_MS = 90_000;
export const CONTRABAND_SITES = [
  { id: "contraband-site-1", sectorId: "frontier", x: 8320, y: 1770 },
  { id: "contraband-site-2", sectorId: "frontier", x: 9110, y: 650 },
  { id: "contraband-site-3", sectorId: "frontier", x: 9270, y: 1580 },
] satisfies { id: string; sectorId: SectorId; x: number; y: number }[];

export interface DeathResolution {
  truceAuthority?: TruceAuthority;
  truceCrime?: TruceCrime;
  truceResolution?: PublicEventResolution;
  terms?: DeathSettlementTerms;
  deathId: string;
  playerId: string;
  runId: string;
  lifeSeq: number;
  sectorId: SectorId;
  x: number;
  y: number;
  cause: "pvp" | "pve";
  killerId: string | null;
  occurredAt: number;
}
export interface CargoLossResult {
  walletLoss?: { credits: string; components: number };
  deathId: string;
  sectorId: SectorId;
  lost: CargoKind[];
  retained: number;
  dropped: number;
  occurredAt: number;
  respawnAt: number;
}
export interface RiskState {
  lifeSeq: number;
  dead: boolean;
  respawnAt: number;
  lastLoss: CargoLossResult | null;
}
export type SharedCargoDrop = CargoPayload & {
  id: string;
  sectorId: SectorId;
  x: number;
  y: number;
  expiresAt: number;
}
export type RiskOperation = (
  | (DeathResolution & { type: "death"; requestId: string })
  | {
      type: "claimDrop";
      requestId: string;
      playerId: string;
      runId: string;
      containerId: string;
    }
  | {
      type: "abandon";
      terms?: ZoneLossTerms;
      requestId: string;
      playerId: string;
      runId: string;
      itemId: string;
      x: number;
      y: number;
      sectorId: SectorId;
    }
  | {
      type: "respawn";
      requestId: string;
      playerId: string;
      runId: string;
      lifeSeq: number;
    }) & { truceAuthority?: TruceAuthority };
export interface RiskPrivateView {
  runId: string;
  state: RiskState;
  cargo: CargoKind[];
}

export function emptyRiskState(): RiskState {
  return { lifeSeq: 0, dead: false, respawnAt: 0, lastLoss: null };
}
